import { useEffect, useMemo, useState } from "react"

import FilterForm from "@/components/FilterForm/FilterForm"
import { FilterSelectState } from "@/components/FilterForm/FilterFormRow"
import Icon from "@/components/Icon"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { ALL_LOCATIONS, ALL_SHERIFF_AREAS, ALL_SHERIFF_TYPES } from "@/constants/sheriffOptions"
import {
  FilterEntry,
  FilterProperty,
  FilterType,
  FilterValue,
  FilterVariant,
} from "@/utils/filtering"

import { Constants } from "../../../../database.types"

const PAY_BASIS_OPTIONS = ["Hourly", "Daily", "Monthly"]

const FILTER_PROPERTIES_BASE: FilterProperty[] = [
  { id: "personId", label: "Person", filterVariant: FilterVariant.Text },
  { id: "email", label: "Email", filterVariant: FilterVariant.Text },
  {
    id: "status",
    label: "Status",
    filterVariant: FilterVariant.Select,
    filterOptions: ["Active", "Inactive", "Banned", "Applied"],
  },
  {
    id: "location",
    label: "Location",
    filterVariant: FilterVariant.Select,
    filterOptions: [...ALL_LOCATIONS],
  },
  {
    id: "sheriffType",
    label: "Sheriff Type",
    filterVariant: FilterVariant.Select,
    filterOptions: [...ALL_SHERIFF_TYPES],
  },
  {
    id: "employer",
    label: "Employer",
    filterVariant: FilterVariant.Select,
    filterOptions: [...Constants.public.Enums.person_employer],
  },
  {
    id: "area",
    label: "Sheriff Areas",
    filterVariant: FilterVariant.Select,
    filterOptions: [...ALL_SHERIFF_AREAS],
  },
  { id: "postcode", label: "Post Code", filterVariant: FilterVariant.Text },
  { id: "stickyLocation", label: "Sticky Location", filterVariant: FilterVariant.Text },
  { id: "driverManagerTeam", label: "Driver Management Team", filterVariant: FilterVariant.Text },
  { id: "contractStartDate", label: "Employment Start Date", filterVariant: FilterVariant.Text },
  { id: "contractEndDate", label: "Employment End Date", filterVariant: FilterVariant.Text },
  { id: "hpdPool", label: "THD Pool", filterVariant: FilterVariant.Text },
  {
    id: "payBasis",
    label: "Pay Basis",
    filterVariant: FilterVariant.Select,
    filterOptions: PAY_BASIS_OPTIONS,
  },
  { id: "payRate", label: "Pay Rate", filterVariant: FilterVariant.Number },
  { id: "dspCrmBase", label: "DSP CRM Base", filterVariant: FilterVariant.Text },
  { id: "contractType", label: "Contract", filterVariant: FilterVariant.Text },
  { id: "drivingLicense", label: "Driving License Number", filterVariant: FilterVariant.Text },
  {
    id: "hasMultipleLocations",
    label: "Has Multiple Locations",
    filterVariant: FilterVariant.Select,
    filterOptions: ["Yes", "No"],
  },
]

interface FilterBarProps {
  filters: FilterEntry[]
  onApplyFilters: (filters: FilterEntry[]) => void
  initialSearch?: string
  onSearch: (term: string) => void
  subTeamOptions: { value: string; label: string }[]
  userRole?: string
}

const getFilterSelectsStates = (filters: FilterEntry[]): FilterSelectState[] => {
  if (!filters.length) {
    return [{ id: "", filterType: FilterType.Is, value: "" }]
  }
  const selects: FilterSelectState[] = []
  for (const filter of filters) {
    for (const filterValue of filter.value) {
      selects.push({
        id: filter.id,
        filterType: filterValue.filterType,
        value: filterValue.value,
      })
    }
  }
  return selects
}

const FilterBar = ({
  filters,
  onApplyFilters,
  initialSearch = "",
  onSearch,
  subTeamOptions,
  userRole,
}: FilterBarProps) => {
  const isDSP = userRole === "DSP"
  const [localSearch, setLocalSearch] = useState(initialSearch)
  const [filtersOpen, setFiltersOpen] = useState(false)

  const [selectsStates, setSelectsStates] = useState<FilterSelectState[]>(() =>
    getFilterSelectsStates(filters),
  )

  useEffect(() => {
    setLocalSearch(initialSearch)
  }, [initialSearch])

  useEffect(() => {
    if (!filtersOpen) {
      setSelectsStates(getFilterSelectsStates(filters))
    }
  }, [filtersOpen, filters])

  const filterProperties = useMemo(() => {
    const subTeamProperty: FilterProperty = {
      id: "subTeam",
      label: "Sub-team",
      filterVariant: FilterVariant.Select,
      filterOptions: subTeamOptions.map((o) => o.value),
    }

    const props = [...FILTER_PROPERTIES_BASE]
    const employerIdx = props.findIndex((p) => p.id === "employer")
    props.splice(employerIdx + 1, 0, subTeamProperty)

    if (isDSP) {
      const hiddenForDSP = ["area", "employer", "subTeam", "sheriffType"]
      return props.filter((p) => !hiddenForDSP.includes(p.id))
    }
    return props
  }, [subTeamOptions, isDSP])

  const activeFilterCount = filters.reduce((count, f) => count + f.value.length, 0)

  const handleSearch = () => {
    onSearch(localSearch)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSearch()
  }

  const clearSearch = () => {
    setLocalSearch("")
    onSearch("")
  }

  const handleApplyFilters = (filterStates: FilterSelectState[]) => {
    const grouped: Record<string, FilterValue[]> = {}
    for (const f of filterStates) {
      if (f.id && typeof f.value === "string" && f.value) {
        if (!grouped[f.id]) grouped[f.id] = []
        grouped[f.id].push({ filterType: f.filterType, value: f.value })
      }
    }

    const entries: FilterEntry[] = Object.entries(grouped).map(([id, value]) => ({ id, value }))
    onApplyFilters(entries)
    setFiltersOpen(false)
  }

  return (
    <div className="flex justify-between flex-1">
      <div className="min-w-80 w-1/2 relative">
        <Icon
          name="magnifyingGlass"
          className="absolute left-3 top-1/2 -translate-y-1/2 text-palette-jungleGreen400 h-4 w-4"
        />
        <Input
          placeholder="Search by name, ID, email..."
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          onKeyDown={handleKeyDown}
          className="pl-10 pr-8 h-10 border-palette-jungleGreen200 focus:border-palette-skyBlue500 focus:ring-palette-skyBlue500"
        />
        {localSearch && (
          <button
            onClick={clearSearch}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-palette-jungleGreen400 hover:text-palette-jungleGreen600 transition-colors"
            aria-label="Clear search"
          >
            <Icon name="close" className="h-4 w-4" />
          </button>
        )}
      </div>

      <Popover open={filtersOpen} onOpenChange={setFiltersOpen}>
        <PopoverTrigger asChild>
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              className="h-10 gap-2 rounded-full border-palette-jungleGreen200 px-5"
            >
              <Icon name="filter" className="h-4 w-4 text-palette-jungleGreen700" />
              Filters
            </Button>
            {activeFilterCount > 0 && (
              <span className="absolute -top-2 -right-2 h-5 min-w-5 rounded-full bg-palette-jungleGreen900 text-white text-xs flex items-center justify-center px-1">
                {activeFilterCount}
              </span>
            )}
          </div>
        </PopoverTrigger>
        <PopoverContent className="lg:w-[48rem] w-[80vw] p-4" align="end">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-palette-jungleGreen900">Filters</h4>
            <button
              onClick={() => setFiltersOpen(false)}
              className="text-palette-jungleGreen400 hover:text-palette-jungleGreen600"
            >
              <Icon name="close" className="h-4 w-4" />
            </button>
          </div>
          <FilterForm
            selectsStates={selectsStates}
            setSelectsStates={setSelectsStates}
            onApply={handleApplyFilters}
            properties={filterProperties}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}

export default FilterBar
